/* eslint-disable prettier/prettier */
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTheme } from 'styled-components'

import { transformJsonConfigIntoObject } from 'plugin-codaf-risk-ext-bowtie/services/barrier'
import { IBarrier, IBarrierTypeIcon } from 'plugin-codaf-risk-ext-bowtie/types'
import { SpecialStatus, UpdateModeMethodId } from 'plugin-codaf-risk-ext-bowtie/types/enums'

import { Portal, Scrollbars } from '../../../../../generic'
import { useBowtieLanguage } from '../../../../../hooks'
import { IReduxActions, IReduxReducers } from '../../../../../store'
import { Icon } from '../../../../../utils'
import { ModalHeader, ModalContent } from './styles'

interface IProps {
    barrierTypeIconMap: Map<number, IBarrierTypeIcon>
    dependentBarriers: IBarrier[]
    bowtieMethodType: string
}

const Modal: React.FC<IProps> = ({ barrierTypeIconMap, dependentBarriers, bowtieMethodType }) => {
    const modalHeight = 423
    const modalWidth = 552

    const theme = useTheme()
    const dispatch = useDispatch()
    const i18n = useBowtieLanguage()

    const { selectedBarrier, color, statusName, event, barrierIds: mandatoryBarrierIds } = useSelector(
        (state: IReduxReducers) => ({
            ...state.barrierSelectedToModal,
            ...state.mandatoryBarrier
        })
    )

    const getBarrierTypeIconFromMap = (id: number): IBarrierTypeIcon => {
        if (id) {
            return barrierTypeIconMap.get(id)
        }

        return null
    }

    const getBarrierSystemId = (bar: IBarrier): string => {
        return (bar.updateModeJsonConfig as any)?.systemId
    }

    return (
        <>
            {selectedBarrier && (
                <Portal
                    onClose={() => dispatch<IReduxActions['barrierSelectedToModal']>({ type: 'CLOSE_MODAL' })}
                    height={modalHeight}
                    width={modalWidth}
                    border={`2px solid ${color}`}
                >
                    <ModalHeader color={color}>
                        <div className="bowtie-modal-title">{i18n(statusName ?? '').toUpperCase()}</div>

                        <div
                            className="bowtie-modal-icons"
                            style={{
                                display:
                                    selectedBarrier.barrierType.barrierTypeSpecial === SpecialStatus.UNMONITORED
                                        ? 'none'
                                        : 'flex'
                            }}
                        >
                            <Icon
                                color="#FFF"
                                withG={false}
                                type={null}
                                style={{ transform: 'scale(1.5)' }}
                                barrierTypeIcon={getBarrierTypeIconFromMap(
                                    selectedBarrier?.barrierType?.barrierTypeIcon?.id
                                )}
                            />

                            {selectedBarrier.criticality?.level && selectedBarrier.criticality.level === 'HIGH' && (
                                <Icon color="#FFF" withG={false} type="critical" style={{ transform: 'scale(1.5)' }} />
                            )}
                            {selectedBarrier.manualInteraction && (
                                <Icon
                                    type="manual"
                                    color={theme.static['gray-0']}
                                    style={{ transform: 'scale(1.5)' }}
                                />
                            )}
                        </div>
                    </ModalHeader>
                    <ModalContent>
                        <div className="bowtie-modal-barrier-name">{selectedBarrier.name}</div>
                        {mandatoryBarrierIds.includes(selectedBarrier.id) && (
                            <div className="bowtie-modal-barrier-details mandatory-icon">
                                <Icon
                                    type="mandatory"
                                    color={theme.static['gray-0']}
                                    className="mandatory-icon"
                                    withG={false}
                                    style={{ transform: 'scale(1.5)' }}
                                />
                                {i18n('Mandatory Barrier')}
                            </div>
                        )}

                        {selectedBarrier.barrierType.barrierTypeSpecial !== SpecialStatus.UNMONITORED &&
                            (selectedBarrier.updateModeCode === UpdateModeMethodId.QUALITATIVE_LIVE_EVENT ||
                                selectedBarrier.updateModeCode === UpdateModeMethodId.QUANTITATIVE_LIVE_EVENT) && (
                                <div className="bowtie-modal-barrier-details">
                                    {i18n('LIVE Event ID')}: {getBarrierSystemId(selectedBarrier)}
                                </div>
                            )}

                        <div className="bowtie-modal-barrier-details">
                            {i18n('Responsible')}: {selectedBarrier.supervisor.name}
                        </div>
                        {selectedBarrier.barrierType.barrierTypeSpecial !== 'UNMONITORED' && (
                            <>
                                <hr style={{ borderColor: theme['gray-900'] }} />

                                {selectedBarrier.method.method === 'QUALITATIVE' && (
                                    <>
                                        {' '}
                                        <div className="bowtie-modal-barrier-details">
                                            QLR:{' '}
                                            {Number.isNaN(Number(event?.qlr))
                                                ? '---'
                                                : (event?.qlr * 100).toFixed().concat('%')}
                                        </div>{' '}
                                    </>
                                )}

                                {(selectedBarrier.method.method === 'QUANTITATIVE' || selectedBarrier.root) &&
                                    bowtieMethodType === 'QUANTITATIVE' && (
                                        <>
                                            {' '}
                                            <div className="bowtie-modal-barrier-details">
                                                PFD inicial:{' '}
                                                {event?.initial_value ? event.initial_value.toExponential(4) : '---'}
                                            </div>
                                            <div className="bowtie-modal-barrier-details">
                                                PFD final: {event?.value ? event.value.toExponential(4) : '---'}
                                            </div>
                                            <div className="bowtie-modal-barrier-details">
                                                Threshold:{' '}
                                                {event?.inactive_threshold
                                                    ? event.inactive_threshold.toExponential(4)
                                                    : '---'}
                                            </div>{' '}
                                        </>
                                    )}

                                {event && (event?.criterios_falha || []).length > 0 && (
                                    <>
                                        <hr style={{ borderColor: theme['gray-900'] }} />
                                        <div className="bowtie-modal-barrier-degradation">
                                            {i18n('Enabled degradation criteria:')}
                                        </div>
                                        <Scrollbars height={70}>
                                            <ul style={{ listStyle: 'initial', paddingLeft: 15 }}>
                                                {event?.criterios_falha?.map((failure, index) => (
                                                    <li key={`failure-criteria-${index}`} style={{ fontSize: 16 }}>
                                                        {failure}
                                                    </li>
                                                ))}
                                            </ul>
                                        </Scrollbars>
                                    </>
                                )}
                            </>
                        )}
                        {selectedBarrier.evidences?.length > 0 && (
                            <>
                                <hr style={{ borderColor: theme['gray-900'] }} />
                                <div className="bowtie-modal-barrier-evidences">{i18n('Evidences')}</div>
                                <Scrollbars height={70}>
                                    <ul style={{ listStyle: 'initial', paddingLeft: 50 }}>
                                        {selectedBarrier.evidences.map((evidence, index) => (
                                            <li key={`evidence-${index}`} style={{ fontSize: 18 }}>
                                                <a href={evidence.url} target="_blank" rel="noreferrer">
                                                    {evidence.alias}
                                                </a>
                                            </li>
                                        ))}
                                    </ul>
                                </Scrollbars>
                            </>
                        )}
                    </ModalContent>
                </Portal>
            )}
        </>
    )
}

export default Modal
